import { ChangeEvent, useEffect, useRef, useState } from 'react';

import {
  AdFieldBathsEnum,
  AdFieldBedsEnum,
  AdFieldFloorEnum,
  AdFieldFurnitureEnum,
  AdFieldOccupancyEnum,
  AdFieldRenovationEnum,
  AdsFilteringType,
  CompletionStatusEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
  UserKindEnum,
  UserModel,
  UserSettingsResponseType,
} from '@/types';
import { useLocationAutocomplete } from '@/components/LocationAutocomplete';
import { LocationModel } from '@/types/location';
import { shouldRestrictByTariff, shouldShowBillingUi } from '@/utils/tariffPolicy';

import { getLabelsAndTexts, predefinedVars } from './AdsFilters.init';
import {
  countNonEmptyFilterFields,
  createFilterStateFromFilteringObj,
  createInitialFilterState,
} from './filterState';
import type {
  AdsFilterChangeOptions,
  CreatorType,
  FilterState,
  HandoverKeys,
  PriceKeys,
  SizeKeys,
} from './filterTypes';
import type { TopAgentFilterValue } from '../utils/adsSearchParams';
import { clearLocationFilter, getLocationSearchFilter } from './locationFilter';
import { prepareMoreFiltersForApply } from './moreFiltersRules';

interface FiltersSnapshot {
  filterState: FilterState;
  filteringObj: AdsFilteringType;
  inputAddressValue: string;
  selectedLocation: LocationModel | null;
}

type BasicFilterPatch = Partial<
  Pick<
    FilterState,
    'locationId' | 'propertyKind' | 'transactionType' | 'rentFrequency' | 'beds' | 'price' | 'size'
  >
>;

type NestedFilterKey = 'size' | 'price' | 'handover' | 'creator';

interface UseAdsFiltersControllerProps {
  filteringObj: AdsFilteringType;
  inputAddressValue: string;
  isRent?: boolean;
  onChangeFilters: (
    value: AdsFilteringType,
    shouldSendRequest?: boolean,
    options?: AdsFilterChangeOptions
  ) => void;
  selectedLocation: LocationModel | null;
  setChangeFilter: (value: boolean) => void;
  setInputAddressValue: (value: string) => void;
  setSelectedLocation: (value: LocationModel | null) => void;
  user: UserModel;
  userSettings: UserSettingsResponseType | null;
  topAgent?: TopAgentFilterValue;
}

export const useAdsFiltersController = ({
  filteringObj,
  inputAddressValue,
  isRent,
  onChangeFilters,
  selectedLocation,
  setChangeFilter,
  setInputAddressValue,
  setSelectedLocation,
  user,
  userSettings,
  topAgent,
}: UseAdsFiltersControllerProps) => {
  const isTariffFree = shouldRestrictByTariff(user);
  const hasSubscriptions = user.hasSubscriptions || false;
  const { areaLabel, bedsBathsText, priceLabel } = getLabelsAndTexts(filteringObj, userSettings);
  const [propertyTypeTab, setPropertyTypeTab] = useState<number>(0);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [counterFilters, setCounterFilters] = useState<number>(0);
  const [filterState, setFilterState] = useState<FilterState>(createInitialFilterState);
  const [moreFiltersDraft, setMoreFiltersDraft] = useState<FilterState | null>(null);
  const [moreFiltersDraftFilteringObj, setMoreFiltersDraftFilteringObj] =
    useState<AdsFilteringType | null>(null);
  const {
    locations: moreFiltersDraftLocations,
    inputAddressValue: moreFiltersDraftInputAddressValue,
    setInputAddressValue: setMoreFiltersDraftInputAddressValue,
    selectedLocation: moreFiltersDraftSelectedLocation,
    setSelectedLocation: setMoreFiltersDraftSelectedLocation,
  } = useLocationAutocomplete({ defaultValue: '' });
  const filtersSnapshotRef = useRef<FiltersSnapshot | null>(null);

  const updateFilterState = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilterState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const updateNestedState = <T extends NestedFilterKey, K extends keyof FilterState[T]>(
    nestedKey: T,
    key: K,
    value: FilterState[T][K]
  ) => {
    setFilterState((prevState) => ({
      ...prevState,
      [nestedKey]: {
        ...prevState[nestedKey],
        [key]: value,
      },
    }));
  };

  const commitBasicFilters = (
    nextFilters: AdsFilteringType,
    statePatch?: BasicFilterPatch,
    options?: AdsFilterChangeOptions
  ) => {
    if (statePatch) {
      setFilterState((prevState) => ({
        ...prevState,
        ...statePatch,
        price: statePatch.price
          ? {
              ...prevState.price,
              ...statePatch.price,
            }
          : prevState.price,
        size: statePatch.size
          ? {
              ...prevState.size,
              ...statePatch.size,
            }
          : prevState.size,
      }));
    }

    onChangeFilters(nextFilters, true, options);
  };

  const updateMoreFiltersDraft = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setMoreFiltersDraft((prevState) =>
      prevState
        ? {
            ...prevState,
            [key]: value,
          }
        : prevState
    );
  };

  const updateMoreFiltersDraftNested = <T extends NestedFilterKey, K extends keyof FilterState[T]>(
    nestedKey: T,
    key: K,
    value: FilterState[T][K]
  ) => {
    setMoreFiltersDraft((prevState) =>
      prevState
        ? {
            ...prevState,
            [nestedKey]: {
              ...prevState[nestedKey],
              [key]: value,
            },
          }
        : prevState
    );
  };

  const discardMoreFiltersDraft = () => {
    setMoreFiltersDraft(null);
    setMoreFiltersDraftFilteringObj(null);
    setMoreFiltersDraftInputAddressValue('');
    setMoreFiltersDraftSelectedLocation(null);
  };

  useEffect(() => {
    setFilterState(createFilterStateFromFilteringObj(filteringObj));
  }, [filteringObj]);

  useEffect(() => {
    setCounterFilters(countNonEmptyFilterFields(filterState) + (topAgent ? 1 : 0));
  }, [filterState, topAgent]);

  const handleInputAddressClear = (isQueryParams?: boolean) => {
    setInputAddressValue('');
    setSelectedLocation(null);
    if (isQueryParams) {
      commitBasicFilters(clearLocationFilter(filteringObj), { locationId: [] });
    } else {
      updateFilterState('locationId', []);
      onChangeFilters(clearLocationFilter(filteringObj));
    }
  };

  const handleInputAddressChange = (value: string) => {
    setSelectedLocation(null);
    updateFilterState('locationId', []);

    if (filteringObj.locationId || filteringObj.address?.length) {
      commitBasicFilters(
        clearLocationFilter(filteringObj),
        { locationId: [] },
        {
          preserveLocationInputValue: value,
        }
      );
    }
  };

  const handleResetFilters = () => {
    filtersSnapshotRef.current = null;
    discardMoreFiltersDraft();
    setInputAddressValue('');
    setSelectedLocation(null);
    commitBasicFilters({ ...predefinedVars.emptyFilteringObj }, undefined, { clearTopAgent: true });
    setChangeFilter(false);
  };

  const propertyKindValueItem = predefinedVars.propertyKindValues
    .filter((item) => filterState?.propertyKind?.includes(item.value))
    .map((item) => item.label)
    .join(', ');
  const propertyKindValueLabel = propertyKindValueItem || 'Property type';

  const myAgency = {
    id: user.kind !== UserKindEnum.Agency ? user?.employer?.id : user.id,
    name: user.kind !== UserKindEnum.Agency ? user?.employer?.name : user.name,
    kind: UserKindEnum.Agency,
  };

  const onSelectLocation = (value: LocationModel, isQueryParams?: boolean) => {
    setSelectedLocation(value);
    const nextFilters = getLocationSearchFilter({
      filteringObj,
      inputAddressValue,
      selectedLocation: value,
    });
    if (isQueryParams) {
      commitBasicFilters(nextFilters, { locationId: [value.id] });
    } else {
      updateFilterState('locationId', [value.id]);
      onChangeFilters(nextFilters);
    }
  };

  const handleMoreFiltersInputAddressClear = () => {
    setMoreFiltersDraftInputAddressValue('');
    setMoreFiltersDraftSelectedLocation(null);
    updateMoreFiltersDraft('locationId', []);
    setMoreFiltersDraftFilteringObj((currentFilter) =>
      clearLocationFilter(currentFilter ?? filteringObj)
    );
  };

  const handleMoreFiltersInputAddressChange = () => {
    setMoreFiltersDraftSelectedLocation(null);
    updateMoreFiltersDraft('locationId', []);
    setMoreFiltersDraftFilteringObj((currentFilter) => {
      const draftFilter = currentFilter ?? filteringObj;

      return draftFilter.locationId || draftFilter.address?.length
        ? clearLocationFilter(draftFilter)
        : draftFilter;
    });
  };

  const onSelectMoreFiltersLocation = (value: LocationModel) => {
    setMoreFiltersDraftSelectedLocation(value);
    updateMoreFiltersDraft('locationId', [value.id]);
    setMoreFiltersDraftFilteringObj((currentFilter) =>
      getLocationSearchFilter({
        filteringObj: currentFilter ?? filteringObj,
        inputAddressValue: moreFiltersDraftInputAddressValue,
        selectedLocation: value,
      })
    );
  };

  const handleChangeRentFrequency = (
    value: RentPeriodEnum,
    _name?: string,
    isQueryParams?: boolean
  ) => {
    const currentFilterState = isQueryParams ? filterState : moreFiltersDraft ?? filterState;
    const newSelection =
      currentFilterState.rentFrequency === value ? undefined : (value as RentPeriodEnum);
    if (isQueryParams) {
      commitBasicFilters(
        { ...filteringObj, rentFrequency: newSelection },
        { rentFrequency: newSelection }
      );
    } else {
      updateMoreFiltersDraft('rentFrequency', newSelection);
    }
  };

  const handleChangeCompletionStatus = (
    value: CompletionStatusEnum,
    _name?: string,
    _isQueryParams?: boolean
  ) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    let newSelection: CompletionStatusEnum[] = [];
    if (currentFilterState.completionStatus.includes(value)) {
      newSelection = currentFilterState.completionStatus.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.completionStatus, value];
    }
    updateMoreFiltersDraft('completionStatus', newSelection);
  };

  const handleChangeTransactionType = (
    value: TransactionTypeEnum,
    _name?: string,
    isQueryParams?: boolean
  ) => {
    const currentFilterState = isQueryParams ? filterState : moreFiltersDraft ?? filterState;
    let newSelection: TransactionTypeEnum[] = [];
    if (currentFilterState.transactionType.includes(value)) {
      newSelection = currentFilterState.transactionType.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.transactionType, value];
    }
    if (isQueryParams) {
      commitBasicFilters(
        { ...filteringObj, transactionType: newSelection },
        { transactionType: newSelection }
      );
    } else {
      updateMoreFiltersDraft('transactionType', newSelection);
    }
  };

  const handleChangePropertyKind = (
    value: PropertyKindEnum,
    _name?: string,
    isQueryParams?: boolean
  ) => {
    const currentFilterState = isQueryParams ? filterState : moreFiltersDraft ?? filterState;
    let newSelection: PropertyKindEnum[] = [];
    if (currentFilterState.propertyKind.includes(value)) {
      newSelection = currentFilterState.propertyKind.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.propertyKind, value];
    }
    if (isQueryParams) {
      commitBasicFilters(
        { ...filteringObj, propertyKind: newSelection },
        { propertyKind: newSelection }
      );
    } else {
      updateMoreFiltersDraft('propertyKind', newSelection);
    }
  };

  const handleChangeSize = (
    event: ChangeEvent<HTMLInputElement>,
    name?: string,
    isQueryParams?: boolean
  ) => {
    const value = event.target.value.replace(/\s/g, '');
    if (name) {
      const keyOf = name as keyof SizeKeys;
      if (isQueryParams) {
        updateNestedState('size', keyOf, value);
      } else {
        updateMoreFiltersDraftNested('size', keyOf, value);
      }
    }
  };

  const handleChangeBeds = (value: AdFieldBedsEnum, _name?: string, isQueryParams?: boolean) => {
    const currentFilterState = isQueryParams ? filterState : moreFiltersDraft ?? filterState;
    let newSelection: AdFieldBedsEnum[] = [];
    if (currentFilterState.beds.includes(value)) {
      newSelection = currentFilterState.beds.filter((item) => item.toString() !== value.toString());
    } else {
      newSelection = [...currentFilterState.beds, value];
    }
    if (isQueryParams) {
      commitBasicFilters({ ...filteringObj, beds: newSelection }, { beds: newSelection });
    } else {
      updateMoreFiltersDraft('beds', newSelection);
    }
  };

  const handleChangePrice = (
    event: ChangeEvent<HTMLInputElement>,
    name?: string,
    isQueryParams?: boolean
  ) => {
    const value = event.target.value.replace(/\s/g, '');
    if (name) {
      const keyOf = name as keyof PriceKeys;
      if (isQueryParams) {
        updateNestedState('price', keyOf, value);
      } else {
        updateMoreFiltersDraftNested('price', keyOf, value);
      }
    }
  };

  const handleCommitBasicPrice = () => {
    const nextPriceMin = filterState.price.priceMin || undefined;
    const nextPriceMax = filterState.price.priceMax || undefined;
    const priceIsUnchanged =
      String(filteringObj.priceMin ?? '') === String(nextPriceMin ?? '') &&
      String(filteringObj.priceMax ?? '') === String(nextPriceMax ?? '');

    if (priceIsUnchanged) return;

    commitBasicFilters(
      {
        ...filteringObj,
        priceMin: nextPriceMin,
        priceMax: nextPriceMax,
      },
      {
        price: {
          priceMin: nextPriceMin,
          priceMax: nextPriceMax,
        },
      }
    );
  };

  const handleCommitBasicSize = () => {
    const nextAreaMinValue = filterState.size.areaMin || undefined;
    const nextAreaMaxValue = filterState.size.areaMax || undefined;
    const nextAreaMin = Number(nextAreaMinValue) || undefined;
    const nextAreaMax = Number(nextAreaMaxValue) || undefined;
    const sizeIsUnchanged =
      Number(filteringObj.areaMin || 0) === Number(nextAreaMin || 0) &&
      Number(filteringObj.areaMax || 0) === Number(nextAreaMax || 0);

    if (sizeIsUnchanged) return;

    commitBasicFilters(
      {
        ...filteringObj,
        areaMin: nextAreaMin,
        areaMax: nextAreaMax,
      },
      {
        size: {
          areaMin: nextAreaMinValue,
          areaMax: nextAreaMaxValue,
        },
      }
    );
  };

  const handleChangeDownPayment = (value: number) => {
    updateMoreFiltersDraft('downPayment', value);
  };

  const handleChangeBaths = (value: AdFieldBathsEnum, _name?: string, _isQueryParams?: boolean) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    let newSelection: AdFieldBathsEnum[] = [];
    if (currentFilterState.baths.includes(value)) {
      newSelection = currentFilterState.baths.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.baths, value];
    }
    updateMoreFiltersDraft('baths', newSelection);
  };

  const handleChangeFurnished = (
    value: AdFieldFurnitureEnum | string,
    _name?: string,
    _isQueryParams?: boolean
  ) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    let newSelection: AdFieldFurnitureEnum[] = [];
    if (value === 'any') {
      return updateMoreFiltersDraft('furniture', [
        AdFieldFurnitureEnum.Furnished,
        AdFieldFurnitureEnum.PartlyFurnished,
        AdFieldFurnitureEnum.Unfurnished,
      ]);
    }

    if (currentFilterState.furniture.includes(value as AdFieldFurnitureEnum)) {
      newSelection = currentFilterState.furniture.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.furniture, value as AdFieldFurnitureEnum];
    }
    updateMoreFiltersDraft('furniture', newSelection);
  };

  const handleChangeRenovation = (
    value: AdFieldRenovationEnum | string,
    _name?: string,
    _isQueryParams?: boolean
  ) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    let newSelection: AdFieldRenovationEnum[] = [];
    if (value === 'any') {
      return updateMoreFiltersDraft('renovation', [
        AdFieldRenovationEnum.Renovation,
        AdFieldRenovationEnum.NonRenovation,
      ]);
    }

    if (currentFilterState.renovation.includes(value as AdFieldRenovationEnum)) {
      newSelection = currentFilterState.renovation.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.renovation, value as AdFieldRenovationEnum];
    }
    updateMoreFiltersDraft('renovation', newSelection);
  };

  const handleChangePostHandover = (value: boolean, _name?: string, _isQueryParams?: boolean) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    const newSelection = currentFilterState.postHandover === value ? undefined : value;
    updateMoreFiltersDraft('postHandover', newSelection);
  };

  const handleChangeHandover = (value: string, name?: string, _isQueryParams?: boolean) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    if (name) {
      const keyOf = name as keyof HandoverKeys;
      const handoverFilterState = currentFilterState.handover[keyOf];
      if (name === 'quarter') {
        const newSelection = handoverFilterState === value ? undefined : value;
        if (currentFilterState.multiHandover.length === 1) {
          updateMoreFiltersDraftNested('handover', keyOf, newSelection);
        }
      } else {
        let newSelection: string[];
        if (currentFilterState.multiHandover.includes(value)) {
          newSelection = currentFilterState.multiHandover.filter((item) => item !== value);
        } else {
          newSelection = [...currentFilterState.multiHandover, value];
        }
        updateMoreFiltersDraft('multiHandover', newSelection);
      }
    }
  };

  const handleChangeFloor = (
    value: AdFieldFloorEnum | string,
    _name?: string,
    _isQueryParams?: boolean
  ) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    let newSelection: AdFieldFloorEnum[] = [];
    if (value === 'any') {
      return updateMoreFiltersDraft('floor', [
        AdFieldFloorEnum.Low,
        AdFieldFloorEnum.Middle,
        AdFieldFloorEnum.High,
      ]);
    }

    if (currentFilterState.floor.includes(value as AdFieldFloorEnum)) {
      newSelection = currentFilterState.floor.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.floor, value as AdFieldFloorEnum];
    }
    updateMoreFiltersDraft('floor', newSelection);
  };

  const handleChangeOccupancy = (
    value: AdFieldOccupancyEnum,
    _name?: string,
    _isQueryParams?: boolean
  ) => {
    const currentFilterState = moreFiltersDraft ?? filterState;
    let newSelection: AdFieldOccupancyEnum[] = [];
    if (currentFilterState.occupancy.includes(value)) {
      newSelection = currentFilterState.occupancy.filter((item) => item !== value);
    } else {
      newSelection = [...currentFilterState.occupancy, value];
    }
    updateMoreFiltersDraft('occupancy', newSelection);
  };

  const handleChangeCreator = (value: CreatorType, _name?: string, _isQueryParams?: boolean) => {
    updateMoreFiltersDraftNested('creator', 'id', value?.id);
    updateMoreFiltersDraftNested('creator', 'name', value?.name);
    updateMoreFiltersDraftNested('creator', 'kind', value?.kind);
  };

  const handleChangeMyAgency = (value: boolean) => {
    updateMoreFiltersDraft('isMyAgency', value);
  };

  const handleChangeMyFriend = (value: boolean) => {
    updateMoreFiltersDraft('myFriends', value);
  };

  const handleChangeKeywords = (event: ChangeEvent<HTMLInputElement>) => {
    updateMoreFiltersDraft('keywords', event.target.value);
  };

  const handleCloseDialog = () => {
    const snapshot = filtersSnapshotRef.current;
    setOpenFilterDialog(false);
    filtersSnapshotRef.current = null;
    discardMoreFiltersDraft();

    if (!snapshot) return;

    setFilterState(snapshot.filterState);
    setInputAddressValue(snapshot.inputAddressValue);
    setSelectedLocation(snapshot.selectedLocation);

    if (filteringObj !== snapshot.filteringObj) {
      onChangeFilters({ ...snapshot.filteringObj });
    }
  };

  const handleOpenFilterDialog = () => {
    filtersSnapshotRef.current = {
      filterState,
      filteringObj,
      inputAddressValue,
      selectedLocation,
    };
    setMoreFiltersDraft(filterState);
    setMoreFiltersDraftFilteringObj(filteringObj);
    setMoreFiltersDraftInputAddressValue(inputAddressValue);
    setMoreFiltersDraftSelectedLocation(selectedLocation);
    setOpenFilterDialog(true);
  };

  const handleShowResult = () => {
    const draftFilterState = moreFiltersDraft ?? filterState;
    const draftFilteringObj = moreFiltersDraftFilteringObj ?? filteringObj;
    const draftInputAddressValue = moreFiltersDraftInputAddressValue;
    const draftSelectedLocation = moreFiltersDraftSelectedLocation;
    filtersSnapshotRef.current = null;

    setFilterState(draftFilterState);
    setInputAddressValue(draftInputAddressValue);
    setSelectedLocation(draftSelectedLocation);
    discardMoreFiltersDraft();

    onChangeFilters(
      prepareMoreFiltersForApply({
        draft: draftFilterState,
        filteringObj: draftFilteringObj,
        isRent,
        myAgency,
      }),
      true
    );
  };

  const rentSalesData = isRent
    ? predefinedVars.rentFrequencyValues
    : predefinedVars.transactionTypeValues;
  const isPriceRangeInvalid =
    Number(filterState.price.priceMin) > Number(filterState.price.priceMax) &&
    Number(filterState.price.priceMax) > 0;
  const isAreaRangeInvalid =
    Number(filterState.size.areaMin) > Number(filterState.size.areaMax) &&
    Number(filterState.size.areaMax) > 0;

  return {
    derived: {
      areaLabel,
      bedsBathsText,
      counterFilters,
      hasSubscriptions,
      isAreaRangeInvalid,
      isPriceRangeInvalid,
      isTariffFree,
      propertyKindValueLabel,
      priceLabel,
      rentSalesData,
      showBillingUi: shouldShowBillingUi(),
    },
    handlers: {
      handleChangeBaths,
      handleChangeBeds,
      handleChangeCompletionStatus,
      handleChangeCreator,
      handleChangeDownPayment,
      handleChangeFloor,
      handleChangeFurnished,
      handleChangeHandover,
      handleChangeKeywords,
      handleChangeMyAgency,
      handleChangeMyFriend,
      handleChangeOccupancy,
      handleChangePostHandover,
      handleChangePrice,
      handleChangePropertyKind,
      handleChangeRenovation,
      handleChangeRentFrequency,
      handleChangeSize,
      handleChangeTransactionType,
      handleCloseDialog,
      handleCommitBasicSize,
      handleCommitBasicPrice,
      handleInputAddressChange,
      handleInputAddressClear,
      handleMoreFiltersInputAddressChange,
      handleMoreFiltersInputAddressClear,
      handleOpenFilterDialog,
      handleResetFilters,
      handleShowResult,
      onSelectLocation,
      onSelectMoreFiltersLocation,
    },
    moreFiltersLocation: {
      inputAddressValue: moreFiltersDraftInputAddressValue,
      locations: moreFiltersDraftLocations,
      selectedLocation: moreFiltersDraftSelectedLocation,
      setInputAddressValue: setMoreFiltersDraftInputAddressValue,
    },
    state: {
      filterState,
      moreFiltersDraft,
      openFilterDialog,
      propertyTypeTab,
      setOpenFilterDialog,
      setPropertyTypeTab,
    },
  };
};
